import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Check, Star, Loader2, Send, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import { API } from '../config/api';
import { isPromoActive, PLAN_PRICING, PROMO_DISCOUNT_LABEL } from '../lib/promo';
import { useLegalVisible } from '../hooks/useLegalVisible';
import ResiliationRequestCard from '../components/ResiliationRequestCard';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';

const PLANS = [
  { id: 'essentielle', name: 'Essentielle', sub: '1 cours / semaine', detail: '4 cours / mois', price: PLAN_PRICING.essentielle.regular, launchPrice: PLAN_PRICING.essentielle.launch, features: ['4 séances par mois', 'Accès à tous les créneaux', 'Réservation flexible'], emotion: 'Idéal pour débuter et installer une routine bien-être.' },
  { id: 'evolution', name: 'Évolution', sub: '2 cours / semaine', detail: '8 cours / mois', price: PLAN_PRICING.evolution.regular, launchPrice: PLAN_PRICING.evolution.launch, recommended: true, features: ['8 séances par mois', 'Progression optimale', 'Priorité de réservation'], emotion: 'Le choix le plus équilibré pour progresser efficacement.' },
  { id: 'transformation', name: 'Transformation', sub: '3 cours / semaine', detail: '12 cours / mois', price: PLAN_PRICING.transformation.regular, launchPrice: PLAN_PRICING.transformation.launch, features: ['12 séances par mois', 'Transformation accélérée', 'Suivi personnalisé'], emotion: 'Pour des résultats visibles et une transformation accélérée.' },
];

export default function Abonnement() {
  const { user, refreshUser } = useAuth();
  const [chosenPlan, setChosenPlan] = useState(null);
  const [saving, setSaving] = useState(null);
  const [recapPlan, setRecapPlan] = useState(null);
  const { visible: legalVisible } = useLegalVisible();
  const [loading, setLoading] = useState(true);
  const [isPrivilege, setIsPrivilege] = useState(false);
  const [mandateStatus, setMandateStatus] = useState(null);
  const [cgvMandate, setCgvMandate] = useState(false);
  const [mandating, setMandating] = useState(false);
  const [subsVisible, setSubsVisible] = useState(false);

  // showDiscount: pendant la fenêtre promo OU si l'utilisateur est marqué offre lancement (privilege)
  const showDiscount = isPromoActive() || isPrivilege;

  useEffect(() => { fetchProfile(); }, []);
  useEffect(() => {
    axios.get(`${API}/public/subscriptions-visible`)
      .then((r) => setSubsVisible(!!r.data.visible))
      .catch(() => setSubsVisible(false));
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setChosenPlan(res.data.chosen_plan || null);
      setIsPrivilege(!!res.data.is_privilege);
      setMandateStatus(res.data.sepa_mandate_status || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChoosePlan = async (planId) => {
    setRecapPlan(null);
    setSaving(planId);
    try {
      await axios.put(`${API}/profile`, { chosen_plan: planId }, { withCredentials: true });
      setChosenPlan(planId);
      await refreshUser(); // refresh AuthContext so the route guard recomputes immediately
      const plan = PLANS.find(p => p.id === planId);
      toast.success('Formule pré-sélectionnée', { description: `Vous avez choisi la formule ${plan?.name}. Elle sera activée à l'ouverture du studio.` });
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally { setSaving(null); }
  };

  const priceFor = (plan) => (showDiscount ? plan.launchPrice : plan.price);

  const handleSignMandate = async () => {
    if (!cgvMandate) { toast.error('Merci d\'accepter les CGV (engagement 12 mois) pour signer votre mandat.'); return; }
    setMandating(true);
    try {
      const res = await axios.post(`${API}/subscriptions/setup-checkout`,
        { plan_id: chosenPlan, origin_url: window.location.origin, cgv_accepted: true },
        { withCredentials: true });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible d\'ouvrir la signature du mandat.');
      setMandating(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      {/* Header */}
      <div className="gradient-cream py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C99A55] mb-2 font-semibold">Mon abonnement</p>
          <h1 className="font-heading font-medium text-3xl sm:text-4xl text-[#493329] tracking-tight">
            {chosenPlan ? 'Votre formule pré-sélectionnée' : 'Choisissez votre formule'}
          </h1>
          <div className="divider-premium" />
          <p className="text-sm text-[#7A6455]">Le paiement sera activé à l'ouverture du studio — septembre 2026.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Privilege banner — visible if discount active */}
        {showDiscount && (
          <Card className="bg-gradient-to-br from-[#C99A55]/15 via-[#C99A55]/8 to-[#F5EFE5] border border-[#C99A55]/40 rounded-2xl mb-6 animate-fade-up" data-testid="privilege-banner">
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#C99A55] flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#9A744C] font-semibold mb-1">Offre de lancement appliquée</p>
                <p className="text-sm text-[#493329] leading-relaxed">
                  Votre <strong>−20%</strong> est bien enregistré sur votre compte. Vous bénéficiez du tarif lancement sur toutes les formules ci-dessous.
                </p>
                <p className="text-xs text-[#7A6455] mt-1.5 italic">
                  Offre valable pendant 12 mois selon les conditions de lancement.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current choice banner */}
        {chosenPlan && (() => {
          const cp = PLANS.find(p => p.id === chosenPlan);
          if (!cp) return null;
          return (
            <Card className="bg-[#493329] text-white rounded-2xl mb-8 animate-fade-up" data-testid="chosen-plan-banner">
              <CardContent className="p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-[#C99A55] text-xs uppercase tracking-widest mb-1 font-semibold">Formule pré-sélectionnée</p>
                    <h2 className="font-heading font-medium text-2xl">{cp.name}</h2>
                    <p className="text-white/50 text-sm mt-1">Sera activée à l'ouverture du studio</p>
                  </div>
                  <div className="text-right">
                    {showDiscount ? (
                      <div className="flex items-baseline justify-end gap-2 flex-wrap">
                        <span className="text-base text-white/40 line-through" data-testid="chosen-plan-old-price">{cp.price}€</span>
                        <span className="text-2xl font-heading font-medium text-[#C99A55]" data-testid="chosen-plan-launch-price">{cp.launchPrice}€</span>
                        <span className="text-sm text-white/40">/ mois</span>
                      </div>
                    ) : (
                      <p className="text-2xl font-heading font-medium">{cp.price}€<span className="text-sm text-white/40"> / mois</span></p>
                    )}
                    {showDiscount && (
                      <p className="text-[10px] uppercase tracking-widest text-[#C99A55] mt-1 font-medium" data-testid="chosen-plan-discount-badge">
                        −20% offre de lancement
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((plan, i) => {
            const isCurrent = chosenPlan === plan.id;
            return (
              <Card
                key={plan.id}
                className={`relative rounded-2xl overflow-hidden card-premium animate-fade-up ${
                  plan.recommended ? 'border-2 border-[#C99A55] shadow-lg shadow-[#C99A55]/8' : 'border border-[#E9DDCC]'
                } ${isCurrent ? 'ring-2 ring-[#8C9B86]' : ''}`}
                style={{ animationDelay: `${0.1 + i * 0.1}s` }}
                data-testid={`sub-plan-${plan.id}`}
              >
                {plan.recommended && (
                  <div className="bg-[#C99A55] text-white text-xs text-center py-2 uppercase tracking-widest flex items-center justify-center gap-1.5 font-medium">
                    <Star className="w-3 h-3" fill="currentColor" /> Le plus populaire
                  </div>
                )}
                {isCurrent && (
                  <div className="bg-[#8C9B86] text-white text-xs text-center py-2 uppercase tracking-widest font-medium">Votre choix actuel</div>
                )}
                <CardContent className="p-7">
                  <h3 className="font-heading font-medium text-2xl text-[#493329] mb-0.5">{plan.name}</h3>
                  <p className="text-sm text-[#C99A55] font-medium">{plan.sub}</p>
                  <p className="text-xs text-[#7A6455]">{plan.detail}</p>

                  <div className="my-5">
                    {showDiscount ? (
                      <>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-base text-[#AE9885] line-through decoration-[#C99A55]/40" data-testid={`sub-plan-old-${plan.id}`}>{plan.price}€</span>
                          <span className="text-4xl font-heading font-medium text-[#493329]" data-testid={`sub-plan-launch-${plan.id}`}>{plan.launchPrice}</span>
                          <span className="text-[#7A6455]">€ / mois</span>
                        </div>
                        <p className="text-[10px] uppercase tracking-widest text-[#C99A55] font-medium mt-1.5" data-testid={`sub-plan-discount-label-${plan.id}`}>
                          {PROMO_DISCOUNT_LABEL}
                        </p>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-heading font-medium text-[#493329]">{plan.price}</span>
                        <span className="text-[#7A6455] ml-1">€ / mois</span>
                      </>
                    )}
                    <p className="text-xs text-[#AE9885] mt-1">Engagement 12 mois</p>
                  </div>

                  <div className="space-y-2.5 mb-5">
                    {plan.features.map((f, j) => (
                      <div key={j} className="flex items-center gap-2.5">
                        <div className="w-4 h-4 rounded-full bg-[#C99A55]/15 flex items-center justify-center flex-shrink-0">
                          <Check className="w-2.5 h-2.5 text-[#C99A55]" strokeWidth={2.5} />
                        </div>
                        <span className="text-sm text-[#493329]">{f}</span>
                      </div>
                    ))}
                  </div>

                  <p className="text-xs text-[#AE9885] italic mb-5">{plan.emotion}</p>

                  <Button
                    onClick={() => setRecapPlan(plan)}
                    disabled={saving === plan.id || isCurrent}
                    className={`w-full rounded-full h-12 btn-premium transition-all duration-400 ${
                      isCurrent ? 'bg-[#E9DDCC] text-[#7A6455] cursor-default' :
                      plan.recommended ? 'bg-[#C99A55] hover:bg-[#493329] text-white' : 'bg-[#493329] hover:bg-[#C99A55] text-white'
                    }`}
                    data-testid={`subscribe-btn-${plan.id}`}
                  >
                    {saving === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> :
                     isCurrent ? 'Formule sélectionnée' : 'Choisir cette formule'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Mandat SEPA — signature du prélèvement (BLOC 3), masqué tant que non publié */}
        {chosenPlan && subsVisible && (
          <div className="mt-12 max-w-xl mx-auto" data-testid="sepa-mandate-section">
            {mandateStatus === 'signed' ? (
              <Card className="bg-white border-[#C99A55]/40 rounded-2xl">
                <CardContent className="p-6 text-center space-y-2">
                  <Check className="w-7 h-7 text-[#C99A55] mx-auto" strokeWidth={1.8} />
                  <h3 className="font-heading text-lg text-[#493329]">Mandat de prélèvement signé</h3>
                  <p className="text-sm text-[#7A6455]">Votre mandat SEPA est enregistré. Aucun prélèvement ne sera effectué avant la date fixée par le studio. Vous serez prévenue avant le premier prélèvement.</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border-[#E9DDCC] rounded-2xl card-premium">
                <CardContent className="p-6 space-y-4">
                  <div className="text-center">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#C99A55] font-semibold mb-1">Dernière étape</p>
                    <h3 className="font-heading text-lg text-[#493329]">Signer mon mandat de prélèvement SEPA</h3>
                    <p className="text-sm text-[#7A6455] mt-1">
                      Vous serez redirigée vers la page sécurisée Stripe pour enregistrer votre mandat (prélèvement bancaire, carte en secours).
                      <strong> Aucun prélèvement maintenant</strong> — uniquement le mandat.
                    </p>
                  </div>
                  <label className="flex items-start gap-2.5 cursor-pointer text-sm text-[#493329] bg-[#F5EFE5] rounded-xl p-3">
                    <input type="checkbox" checked={cgvMandate} onChange={(e) => setCgvMandate(e.target.checked)}
                      className="mt-0.5" data-testid="mandate-cgv-checkbox" />
                    <span>
                      J'accepte les <a href="/legal/cgv" target="_blank" rel="noopener noreferrer" className="text-[#C99A55] underline">Conditions Générales de Vente</a> et je m'engage pour <strong>12 mois</strong> à compter du premier prélèvement. <span className="text-[#C99A55]">*</span>
                    </span>
                  </label>
                  <Button onClick={handleSignMandate} disabled={mandating || !cgvMandate}
                    className="w-full rounded-full h-12 bg-[#C99A55] hover:bg-[#493329] text-white disabled:opacity-50"
                    data-testid="sign-mandate-btn">
                    {mandating ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Signer mon mandat SEPA</>}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {subsVisible && <ResiliationRequestCard />}

        {/* Options complémentaires */}
        <div className="mt-14">
          <h2 className="font-heading font-medium text-2xl text-[#493329] text-center mb-6">Options complémentaires</h2>
          <div className="grid grid-cols-1 max-w-md mx-auto">
            <Card className="bg-white border-[#E9DDCC] rounded-2xl card-premium">
              <CardContent className="p-6">
                <h3 className="font-heading font-medium text-lg text-[#493329] mb-1">Cours à l'unité</h3>
                <p className="text-2xl font-heading font-medium text-[#493329] mb-1">35 <span className="text-sm text-[#7A6455]">€</span></p>
                <p className="text-xs text-[#AE9885] mb-4">Sans engagement · Disponible à l'ouverture</p>
                <Button disabled variant="outline" className="w-full border-[#E9DDCC] text-[#AE9885] rounded-full h-11 cursor-default">
                  Bientôt disponible
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Expérience privée — invitation discrète */}
        <div className="mt-12 max-w-xl mx-auto text-center" data-testid="private-experience-invite-dashboard">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C99A55] font-semibold mb-2">Sur invitation</p>
          <h3 className="font-heading font-light text-xl text-[#493329] mb-2">Expérience privée PURO</h3>
          <p className="text-sm text-[#7A6455] leading-relaxed max-w-md mx-auto mb-4">
            Un accompagnement privé et personnalisé, étudié au cas par cas.
          </p>
          <Link to="/cours-prive" className="text-sm text-[#9A744C] underline underline-offset-4 decoration-[#C99A55]/40 hover:text-[#C99A55] transition-colors">
            Faire une demande privée
          </Link>
        </div>

        <p className="text-center text-xs text-[#AE9885] mt-10 italic">Aucun prélèvement ne sera effectué avant l'ouverture du studio.</p>
      </div>

      {/* Récapitulatif pré-contractuel (conformité) */}
      <Dialog open={!!recapPlan} onOpenChange={(o) => !o && setRecapPlan(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="recap-dialog">
          {recapPlan && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-[#493329]">Récapitulatif avant validation</DialogTitle>
                <DialogDescription className="text-xs text-[#7A6455]">Informations précontractuelles — à lire avant de confirmer.</DialogDescription>
              </DialogHeader>
              <div className="rounded-2xl border border-[#E9DDCC] bg-[#F5EFE5] p-4 space-y-2 text-sm text-[#493329]" data-testid="recap-content">
                <p><span className="text-[#7A6455]">Formule :</span> <strong>{recapPlan.name}</strong> — {recapPlan.detail}</p>
                <p><span className="text-[#7A6455]">Prix mensuel TTC :</span> <strong>{priceFor(recapPlan)}€/mois</strong></p>
                <p><span className="text-[#7A6455]">Engagement :</span> 12 mois à compter de l'ouverture du studio</p>
                <p><span className="text-[#7A6455]">Après 12 mois :</span> abonnement sans engagement, résiliable chaque fin de mois</p>
                <p><span className="text-[#7A6455]">Résiliation :</span> en ligne depuis votre espace (motif légitime possible pendant l'engagement)</p>
                <p><span className="text-[#7A6455]">Rétractation :</span> 14 jours{legalVisible && <> — <Link to="/legal/retractation" className="text-[#C99A55] underline" target="_blank">détails et formulaire</Link></>}</p>
                <p className="text-[11px] text-[#AE9885] pt-1">Aucun prélèvement avant l'ouverture. Le mandat de prélèvement sera proposé ultérieurement.{legalVisible && <> <Link to="/legal/cgv" className="underline" target="_blank">CGV</Link></>}</p>
              </div>
              <Button onClick={() => handleChoosePlan(recapPlan.id)} disabled={saving === recapPlan.id}
                className="w-full rounded-full h-12 bg-[#C99A55] hover:bg-[#493329] text-white" data-testid="recap-confirm-btn">
                {saving === recapPlan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Je confirme mon choix'}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
